import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, Ban, DollarSign, Calendar, FileText, Printer } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const BankVoucher = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [vouchers,      setVouchers]      = useState([]);
  const [bankAccounts,  setBankAccounts]  = useState([]);
  const [otherAccounts, setOtherAccounts] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [voucherMode,   setVoucherMode]   = useState('payment');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors,        setErrors]        = useState({});

  const [formData, setFormData] = useState({
    acjournalid: null, companyid: companyId || null, fyearid: yearId || null,
    voucherno: '', transdate: new Date().toISOString().split('T')[0],
    vouchertype: 'BV', remarks: '', bankAccountId: '', details: [],
    bankDetails: { bankname: '', chequeno: '', chequedate: new Date().toISOString().split('T')[0], partyname: '' },
  });

  const [currentDetail, setCurrentDetail] = useState({ acheadid: '', amount: '', remarks: '' });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchInitialData();
  }, [companyId, yearId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try { await Promise.all([fetchBankAccounts(), fetchOtherAccounts(), fetchVouchers()]); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await apiClient.get('/Acheads.php', { params: { companyid: companyId, accounttype: 'Bank' } });
      if (res.data.success) setBankAccounts(res.data.data || []);
    } catch (err) { if (err.response?.status === 401) alert('Session expired. Please login again.'); }
  };

  const fetchOtherAccounts = async () => {
    try {
      const res = await apiClient.get('/Acheads.php', { params: { companyid: companyId, excludetype: 'Bank' } });
      if (res.data.success) setOtherAccounts(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchVouchers = async () => {
    try {
      const res = await apiClient.get('/Vouchers.php', { params: { companyid: companyId, fyearid: yearId, vouchertype: voucherMode === 'payment' ? 'BV' : 'BR' } });
      if (res.data.success) setVouchers(res.data.data || []);
    } catch (err) { if (err.response?.status === 401) alert('Session expired. Please login again.'); }
  };

  const getNextVoucherNumber = async () => {
    try {
      const voucherType = voucherMode === 'payment' ? 'BV' : 'BR';
      const res = await apiClient.get('/Vouchers.php', { params: { nextvoucher: true, companyid: companyId, fyearid: yearId, vouchertype: voucherType } });
      if (res.data.success) setFormData(p => ({ ...p, voucherno: res.data.data.voucherno, vouchertype: voucherType }));
    } catch (e) { console.error(e); }
  };

  const handleVoucherModeChange = (mode) => {
    setVoucherMode(mode);
    setFormData(p => ({ ...p, vouchertype: mode === 'payment' ? 'BV' : 'BR' }));
  };

  const handleNew = async () => { resetForm(); await getNextVoucherNumber(); setEditMode(false); setShowModal(true); };

  const handleEdit = async (voucher) => {
    try {
      const res = await apiClient.get('/Vouchers.php', { params: { id: voucher.acjournalid } });
      if (res.data.success) {
        const { master, details, bankDetails } = res.data.data;
        setFormData({ acjournalid: master.acjournalid, companyid: master.companyid, fyearid: master.fyearid, voucherno: master.voucherno, transdate: master.transdate, vouchertype: master.vouchertype, remarks: master.remarks || '', bankAccountId: details.length > 0 ? details[0].acheadid : '', details: details.slice(1), bankDetails: bankDetails || { bankname: '', chequeno: '', chequedate: new Date().toISOString().split('T')[0], partyname: '' } });
        setVoucherMode(master.vouchertype === 'BR' ? 'receipt' : 'payment');
        setEditMode(true); setShowModal(true);
      }
    } catch { alert('Error loading voucher details'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this voucher?')) return;
    try {
      const res = await apiClient.delete('/Vouchers.php', { data: { acjournalid: id } });
      if (res.data.success) { alert('Voucher deleted successfully!'); fetchVouchers(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting voucher. Please try again.');
    }
  };

  const handlePrint = () => {
    if (formData.details.length === 0) { alert('Please add transaction details before printing'); return; }
    if (!formData.bankAccountId) { alert('Please select a bank account before printing'); return; }
    const bankAccount = bankAccounts.find(a => a.acheadid === parseInt(formData.bankAccountId));
    const voucherTypeText = voucherMode === 'payment' ? 'Bank Payment' : 'Bank Receipt';
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Bank Voucher - ${formData.voucherno}</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;padding:20mm;background:white;}.print-header{text-align:center;margin-bottom:30px;border-bottom:3px solid #000;padding-bottom:20px;}.company-name{font-size:24px;font-weight:bold;margin-bottom:5px;}.voucher-title{font-size:18px;font-weight:bold;margin-top:10px;color:${voucherMode==='payment'?'#991b1b':'#065f46'};}.voucher-info{display:flex;justify-content:space-between;margin:20px 0;padding:15px;background:#f5f5f5;border:1px solid #ddd;}.info-item{display:flex;flex-direction:column;}.info-label{font-size:11px;color:#666;text-transform:uppercase;margin-bottom:4px;}.info-value{font-size:14px;font-weight:bold;font-family:'Courier New',monospace;}.bank-account-box{background:${voucherMode==='payment'?'#fee2e2':'#d1fae5'};border-left:4px solid ${voucherMode==='payment'?'#dc2626':'#059669'};padding:15px;margin:20px 0;}.bank-account-label{font-size:11px;color:#666;text-transform:uppercase;margin-bottom:8px;font-weight:bold;}.bank-account-name{font-size:16px;font-weight:bold;color:#1f2937;}.details-table{width:100%;border-collapse:collapse;margin:20px 0;}.details-table th{background:#000;color:white;padding:12px 8px;text-align:left;font-size:12px;text-transform:uppercase;font-weight:bold;}.details-table td{padding:10px 8px;border-bottom:1px solid #ddd;font-size:13px;}.details-table .amount{text-align:right;font-family:'Courier New',monospace;font-weight:bold;color:${voucherMode==='payment'?'#dc2626':'#059669'};}.totals-section{margin:20px 0;border-top:3px double #000;padding-top:15px;}.total-row{display:flex;justify-content:space-between;padding:12px 15px;font-size:18px;font-weight:bold;background:${voucherMode==='payment'?'#fee2e2':'#d1fae5'};border:2px solid ${voucherMode==='payment'?'#dc2626':'#059669'};border-radius:8px;}.total-value{font-family:'Courier New',monospace;font-weight:bold;color:${voucherMode==='payment'?'#991b1b':'#065f46'};}.signatures{display:flex;justify-content:space-between;margin-top:60px;padding-top:20px;border-top:1px solid #ddd;}.signature-box{text-align:center;width:200px;}.signature-line{border-top:2px solid #000;margin-top:60px;margin-bottom:8px;}.signature-label{font-size:12px;color:#666;}.footer{margin-top:40px;text-align:center;font-size:11px;color:#999;border-top:1px solid #ddd;padding-top:15px;}</style></head><body><div class="print-header"><div class="company-name">${session?.companyname||'Company Name'}</div><div class="voucher-title">${voucherTypeText.toUpperCase()} VOUCHER</div></div><div class="voucher-info"><div class="info-item"><span class="info-label">Voucher No</span><span class="info-value">${formData.voucherno}</span></div><div class="info-item"><span class="info-label">Date</span><span class="info-value">${new Date(formData.transdate).toLocaleDateString('en-GB')}</span></div><div class="info-item"><span class="info-label">Type</span><span class="info-value">${voucherMode==='payment'?'BV':'BR'}</span></div></div><div class="bank-account-box"><div class="bank-account-label">${voucherMode==='payment'?'Paid From Bank Account':'Received To Bank Account'}</div><div class="bank-account-name">${bankAccount?.acheadname||'Bank Account'}</div></div><table class="details-table"><thead><tr><th>#</th><th>Account Name</th><th style="text-align:right">Amount</th><th>Remarks</th></tr></thead><tbody>${formData.details.map((d,i)=>`<tr><td style="text-align:center">${i+1}</td><td style="font-weight:600">${d.acheadname}</td><td class="amount">₹ ${parseFloat(d.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td style="font-style:italic;color:#666">${d.remarks||'-'}</td></tr>`).join('')}</tbody></table><div class="totals-section"><div class="total-row"><span class="total-label">Total Amount:</span><span class="total-value">₹ ${calculateTotal().toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div></div>${formData.remarks?`<div style="margin:20px 0;padding:15px;background:#f9f9f9;border-left:4px solid #667eea"><div style="font-size:11px;color:#666;text-transform:uppercase;margin-bottom:8px;font-weight:bold">Remarks</div><div style="font-size:13px;line-height:1.6">${formData.remarks}</div></div>`:''}<div class="signatures"><div class="signature-box"><div class="signature-line"></div><div class="signature-label">Prepared By</div></div><div class="signature-box"><div class="signature-line"></div><div class="signature-label">Verified By</div></div><div class="signature-box"><div class="signature-line"></div><div class="signature-label">Approved By</div></div></div><div class="footer"><p>This is a computer-generated document. No signature is required.</p><p>Printed on: ${new Date().toLocaleString('en-GB')}</p></div></body></html>`);
    printWindow.document.close(); printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const addDetailLine = () => {
    if (!currentDetail.acheadid || !currentDetail.amount) { alert('Please select an account and enter an amount'); return; }
    const account = otherAccounts.find(a => a.acheadid === parseInt(currentDetail.acheadid));
    if (!account) { alert('Invalid account selected'); return; }
    setFormData(p => ({ ...p, details: [...p.details, { acheadid: parseInt(currentDetail.acheadid), acheadname: account.acheadname, amount: parseFloat(currentDetail.amount), remarks: currentDetail.remarks }] }));
    setCurrentDetail({ acheadid: '', amount: '', remarks: '' });
  };

  const removeDetailLine = (index) => setFormData(p => ({ ...p, details: p.details.filter((_, i) => i !== index) }));

  const calculateTotal = () => formData.details.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

  const validateForm = () => {
    const errs = {};
    if (!formData.voucherno.trim())  errs.voucherno    = 'Voucher number is required';
    if (!formData.transdate)         errs.transdate    = 'Transaction date is required';
    if (!formData.bankAccountId)     errs.bankAccountId = 'Bank account is required';
    if (formData.details.length === 0) errs.details   = 'At least one detail line is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const bankAmt = voucherMode === 'receipt' ? calculateTotal() : -calculateTotal();
      const payload = { companyid: companyId, fyearid: yearId, voucherno: formData.voucherno, transdate: formData.transdate, vouchertype: formData.vouchertype, remarks: formData.remarks, details: [{ acheadid: parseInt(formData.bankAccountId), amount: bankAmt, remarks: 'Bank Account' }, ...formData.details.map(d => ({ acheadid: d.acheadid, amount: voucherMode === 'receipt' ? -d.amount : d.amount, remarks: d.remarks || '' }))], bankdetails: formData.bankDetails };
      if (editMode) payload.acjournalid = formData.acjournalid;
      const res = await apiClient[editMode ? 'put' : 'post']('/Vouchers.php', payload);
      if (res.data.success) { alert(editMode ? 'Voucher updated successfully!' : 'Voucher created successfully!'); setShowModal(false); resetForm(); fetchVouchers(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving voucher. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const resetForm = () => {
    setFormData({ acjournalid: null, companyid: companyId || null, fyearid: yearId || null, voucherno: '', transdate: new Date().toISOString().split('T')[0], vouchertype: voucherMode === 'payment' ? 'BV' : 'BR', remarks: '', bankAccountId: '', details: [], bankDetails: { bankname: '', chequeno: '', chequedate: new Date().toISOString().split('T')[0], partyname: '' } });
    setCurrentDetail({ acheadid: '', amount: '', remarks: '' });
    setErrors({});
  };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const isPayment  = voucherMode === 'payment';
  const filteredVouchers = vouchers.filter(v => v.voucherno?.toLowerCase().includes(searchTerm.toLowerCase()) || v.remarks?.toLowerCase().includes(searchTerm.toLowerCase()));

  const inputCls  = (err) => `w-full px-3 py-2.5 border rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${err ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;
  const selectCls = (err) => inputCls(err) + ' cursor-pointer';
  const labelCls  = 'block mb-1.5 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  // ── no session ─────────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <DollarSign size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex flex-col gap-3">
          <h1 className="text-xl font-bold m-0" style={{ color: '#fff' }}>Bank Voucher</h1>
          {/* Mode toggle */}
          <div className="flex gap-1 bg-white/20 p-1 rounded-lg">
            {['payment', 'receipt'].map(mode => (
              <button key={mode} onClick={() => handleVoucherModeChange(mode)}
                className={`px-5 py-1.5 text-sm font-semibold rounded-md border-0 cursor-pointer transition-all duration-150 ${
                  voucherMode === mode
                    ? 'bg-white shadow-sm'
                    : 'bg-transparent text-white hover:bg-white/20'
                }`}
                style={voucherMode === mode ? { color: NAVY } : {}}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <button onClick={handleNew}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> New Voucher
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search vouchers..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredVouchers.length} vouchers
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading vouchers...</div>
        ) : filteredVouchers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <FileText size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No vouchers found</p>
            {searchTerm && <button onClick={() => setSearchTerm('')} className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Voucher No', 'Date', 'Type', 'Amount', 'Remarks', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredVouchers.map(voucher => (
                  <tr key={voucher.acjournalid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">{voucher.voucherno}</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{new Date(voucher.transdate).toLocaleDateString()}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase ${
                        voucher.vouchertype === 'BR'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {voucher.voucher_description}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right font-mono font-semibold text-gray-700">
                      {parseFloat(voucher.total_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">{voucher.remarks || '-'}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(voucher)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(voucher.acjournalid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[960px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                {editMode ? 'Edit' : 'New'} {isPayment ? 'Bank Payment' : 'Bank Receipt'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7 space-y-5">

              {/* ── Voucher Info ── */}
              <div className="px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">Voucher Information</div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>
                      Voucher No
                      <span className="ml-2 inline-block px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded uppercase">Auto</span>
                    </label>
                    <div className="relative flex items-center">
                      <input type="text" value={formData.voucherno} readOnly placeholder="Auto-generating..."
                        className="w-full pr-8 px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-mono font-semibold bg-gray-100 text-gray-700 cursor-not-allowed outline-none" />
                      <span className="absolute right-2.5 text-lg opacity-50 pointer-events-none">🔒</span>
                    </div>
                    {errMsg(errors.voucherno)}
                  </div>
                  <div>
                    <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                    <input type="date" value={formData.transdate}
                      onChange={e => setFormData({ ...formData, transdate: e.target.value })}
                      className={inputCls(errors.transdate)} />
                    {errMsg(errors.transdate)}
                  </div>
                  <div>
                    <label className={labelCls}>Bank Account <span className="text-red-500">*</span></label>
                    <select value={formData.bankAccountId}
                      onChange={e => setFormData({ ...formData, bankAccountId: e.target.value })}
                      className={selectCls(errors.bankAccountId)}>
                      <option value="">-- Select Bank Account --</option>
                      {bankAccounts.map(a => <option key={a.acheadid} value={a.acheadid}>{a.acheadname} ({a.acheadcode})</option>)}
                    </select>
                    {errMsg(errors.bankAccountId)}
                  </div>
                </div>
              </div>

              {/* ── Cheque Details ── */}
              <div className="px-4 py-4 bg-amber-50 border border-amber-200 border-l-4 border-l-amber-400 rounded-r-xl">
                <div className="text-[11px] font-bold text-amber-700 uppercase tracking-widest mb-4">Cheque Details</div>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>Bank Name</label>
                    <input type="text" value={formData.bankDetails.bankname} placeholder="Bank Name"
                      onChange={e => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, bankname: e.target.value } })}
                      className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>Cheque No</label>
                    <input type="text" value={formData.bankDetails.chequeno} placeholder="Cheque Number"
                      onChange={e => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, chequeno: e.target.value } })}
                      className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>Cheque Date</label>
                    <input type="date" value={formData.bankDetails.chequedate}
                      onChange={e => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, chequedate: e.target.value } })}
                      className={inputCls(false)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Party Name</label>
                  <input type="text" value={formData.bankDetails.partyname} placeholder="Party/Payee Name"
                    onChange={e => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, partyname: e.target.value } })}
                    className={inputCls(false)} />
                </div>
              </div>

              {/* ── Detail Lines ── */}
              <div className="px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">
                  Details <span className="text-red-500">*</span>
                </div>

                {/* Add row */}
                <div className="flex gap-2 mb-3 items-start">
                  <select value={currentDetail.acheadid}
                    onChange={e => setCurrentDetail({ ...currentDetail, acheadid: e.target.value })}
                    className="flex-[2] px-2.5 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none cursor-pointer focus:border-[#0d1c6b]">
                    <option value="">-- Select Account --</option>
                    {otherAccounts.map(a => <option key={a.acheadid} value={a.acheadid}>{a.acheadname}</option>)}
                  </select>
                  <input type="number" value={currentDetail.amount} placeholder="Amount" step="0.01"
                    onChange={e => setCurrentDetail({ ...currentDetail, amount: e.target.value })}
                    className="flex-1 px-2.5 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none text-right font-mono focus:border-[#0d1c6b]" />
                  <input type="text" value={currentDetail.remarks} placeholder="Remarks"
                    onChange={e => setCurrentDetail({ ...currentDetail, remarks: e.target.value })}
                    className="flex-[2] px-2.5 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:border-[#0d1c6b]" />
                  <button type="button" onClick={addDetailLine}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border-0 cursor-pointer bg-emerald-500 text-white flex-shrink-0 hover:bg-emerald-600 transition-colors duration-150">
                    <Plus size={18} />
                  </button>
                </div>

                {errMsg(errors.details)}

                {/* Detail table */}
                {formData.details.length > 0 && (
                  <div className="overflow-x-auto mt-2 rounded-lg border border-gray-200">
                    <table className="w-full border-collapse text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          {['Account', 'Amount', 'Remarks', ''].map(h => (
                            <th key={h} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-gray-200">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {formData.details.map((detail, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-3 py-2.5 text-gray-800 font-medium">{detail.acheadname}</td>
                            <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-700">{parseFloat(detail.amount).toFixed(2)}</td>
                            <td className="px-3 py-2.5 text-gray-500 text-xs">{detail.remarks || '-'}</td>
                            <td className="px-3 py-2.5 text-center">
                              <button type="button" onClick={() => removeDetailLine(index)}
                                className="p-1 rounded border-0 cursor-pointer bg-transparent text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors duration-150">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                          <td className="px-3 py-3 text-gray-700">Total</td>
                          <td className="px-3 py-3 text-right font-mono text-emerald-700">{calculateTotal().toFixed(2)}</td>
                          <td colSpan={2} />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Remarks */}
              <div>
                <label className={labelCls}>Remarks</label>
                <textarea value={formData.remarks} rows={3} placeholder="Enter any additional remarks..."
                  onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] resize-y font-[inherit]" />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  <Ban size={14} /> Cancel
                </button>
                <button type="button" onClick={handlePrint}
                  disabled={formData.details.length === 0 || !formData.bankAccountId}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                  <Printer size={14} /> Print
                </button>
                <button type="submit" disabled={submitLoading || formData.details.length === 0}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  <Save size={14} /> {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default BankVoucher;